'use client';

import { useTranslations } from 'next-intl';
import React, { useCallback, useEffect, useMemo, useState } from 'react';

import { getCustomStyle } from '@/app/[locale]/(default-layout)/_components/CustomStyles';
import ProgramShowingListSkeleton from '@/app/[locale]/(default-layout)/program/_components/skeletons/ProgramShowingListSkeleton';

import { PageBackgroundLayers } from '@/components/PageBackground';

import { ContentDto } from '@cineamo/api/models/content/ContentDto.types';
import { ShowingDto } from '@cineamo/api/models/showing/ShowingDto.types';

import { hasMatchingGenre, hasMovieAccessibilityFeatures, isMovieReleaseDateNew } from '@/helpers/movie-helper';

import { CachedContentData } from '@/hooks/useCachedContentData';
import { useVirtualPagination } from '@/hooks/useVirtualPagination';
import { useFormat } from '@cineamo/ui/src/hooks/formatter/useFormat';

import { useCinemaRooms } from '@/providers/CinemaRoomProvider';
import { useDomainConfiguration } from '@/providers/DomainConfigurationProvider';
import { useProgramFilterContext } from '@/providers/ProgramFilterProvider';
import { formatDate_yyyyMMdd, isDateToday, isDateWithinCalendarBar } from '@/utils/date-helper';

import { ProgramCompactListItem } from './ProgramCompactListItem';

type ProgramCompactListProps = {
    showings: ShowingDto[];
    contents: CachedContentData[];
    selectedDate: string | 'all';
    hasHighlights?: boolean;
    /** Roomier proportions for wide screens. The row structure is the same either way. */
    isDesktop?: boolean;
};

type ShowingsGroup = {
    date: string | null;
    showings: ShowingDto[];
};

const VIRTUAL_PAGINATION_CONFIG = {
    initialLoad: 10,
    loadMore: 10,
    loadDebounce: 100,
    checkInterval: 200,
    rootMargin: '200px'
} as const;

export function ProgramCompactList(props: ProgramCompactListProps): React.JSX.Element {
    const { selectedDate } = props;

    const t = useTranslations();
    const domainConfiguration = useDomainConfiguration();
    const format = useFormat({ timeZone: domainConfiguration.timeZone });
    const customStyle = getCustomStyle(domainConfiguration);
    const { sorting, activeFilters } = useProgramFilterContext();
    const { getCinemaRoomById } = useCinemaRooms();
    const isNewFilterActive = activeFilters.isNew === 'true';
    const isGretaFilterActive = activeFilters.isGreta === 'true';
    const isWheelchairFilterActive = activeFilters.isWheelchairAccessible === 'true';
    const activeGenreIds = useMemo<string[]>(() => {
        const genreIds = activeFilters.genreIds;
        if (!genreIds) return [];
        return Array.isArray(genreIds) ? genreIds : [genreIds];
    }, [activeFilters.genreIds]);

    const contentById = useMemo(
        () => new Map<ContentDto['id'], CachedContentData>(props.contents.map((content) => [content.id, content])),
        [props.contents]
    );

    const listShowings = useMemo(
        () =>
            props.showings.filter((showing) => {
                const content = contentById.get(showing.contentId);
                if (!content) return false;
                if (isNewFilterActive && !isMovieReleaseDateNew(content.releaseDate)) return false;
                if (isGretaFilterActive && !hasMovieAccessibilityFeatures(content.movieAccessibility)) return false;
                if (isWheelchairFilterActive && !getCinemaRoomById(showing.cinemaRoomId)?.hasWheelchairSpaces) {
                    return false;
                }
                return hasMatchingGenre(content.genres, activeGenreIds);
            }),
        [
            props.showings,
            contentById,
            isNewFilterActive,
            isGretaFilterActive,
            isWheelchairFilterActive,
            getCinemaRoomById,
            activeGenreIds
        ]
    );

    const showingsCountForContentId = useMemo(() => {
        const map = new Map<ContentDto['id'], number>();
        listShowings.forEach((showing) => {
            map.set(showing.contentId, (map.get(showing.contentId) || 0) + 1);
        });
        return map;
    }, [listShowings]);

    const showingSortingFunction = useCallback(
        (a: ShowingDto, b: ShowingDto) => {
            const startDatetimeComparison = new Date(a.startDatetime).getTime() - new Date(b.startDatetime).getTime();

            const order = ['date-desc', 'showings-desc', 'newest-desc', 'z-a'].includes(sorting) ? -1 : 1;
            const contentA = contentById.get(a.contentId);
            const contentB = contentById.get(b.contentId);
            let result = 0;

            switch (sorting) {
                case 'date-asc':
                case 'date-desc': {
                    result = order * startDatetimeComparison;
                    break;
                }
                case 'a-z':
                case 'z-a': {
                    result = order * contentA.title.localeCompare(contentB.title);
                    break;
                }
                case 'showings-asc':
                case 'showings-desc': {
                    result =
                        order *
                        (showingsCountForContentId.get(a.contentId) - showingsCountForContentId.get(b.contentId));
                    break;
                }
                case 'newest-desc':
                case 'newest-asc': {
                    const aReleaseDate = contentA.releaseDate ? new Date(contentA.releaseDate).getTime() : 0;
                    const bReleaseDate = contentB.releaseDate ? new Date(contentB.releaseDate).getTime() : 0;
                    result = order * (aReleaseDate - bReleaseDate);
                    break;
                }
                default:
                    result = startDatetimeComparison;
            }
            return result === 0 ? startDatetimeComparison : result;
        },
        [contentById, showingsCountForContentId, sorting]
    );

    const sortedShowings = useMemo(
        () => listShowings.toSorted(showingSortingFunction),
        [listShowings, showingSortingFunction]
    );

    const isChronologicalSorting = !sorting || sorting === 'date-asc' || sorting === 'date-desc';
    // A selected day beyond the calendar bar's window is not highlighted anywhere,
    // so the showings have to name their date - same as when all days are mixed.
    const isSelectedDateVisibleInCalendarBar = selectedDate !== 'all' && isDateWithinCalendarBar(selectedDate);
    const showDateHeaders = selectedDate === 'all' && isChronologicalSorting;
    const showDatePerItem = !isSelectedDateVisibleInCalendarBar && !showDateHeaders;

    const groups = useMemo<ShowingsGroup[]>(() => {
        if (!showDateHeaders) {
            return [{ date: null, showings: sortedShowings }];
        }
        const grouped: ShowingsGroup[] = [];
        sortedShowings.forEach((showing) => {
            const date = formatDate_yyyyMMdd(showing.startDatetime);
            const lastGroup = grouped[grouped.length - 1];
            if (lastGroup?.date === date) {
                lastGroup.showings.push(showing);
            } else {
                grouped.push({ date, showings: [showing] });
            }
        });
        return grouped;
    }, [sortedShowings, showDateHeaders]);

    const { visibleCount, loadMoreRef, hasMore } = useVirtualPagination({
        items: groups,
        config: VIRTUAL_PAGINATION_CONFIG,
        getItemsFromGroup: (group) => group.showings
    });

    // The sticky top banners (app banner/info banner, min 48px, grow with wrapping text) would
    // cover the sticky date headers, so they stick right below the measured banner height.
    const [stickyTopOffset, setStickyTopOffset] = useState<number>(0);
    useEffect(() => {
        if (!showDateHeaders) {
            return;
        }
        const banners = ['__cineamo_app_banner__', '__info_banner__']
            .map((id) => document.getElementById(id))
            .filter((element): element is HTMLElement => !!element);
        if (banners.length === 0) {
            return;
        }
        const updateOffset = () => setStickyTopOffset(Math.max(...banners.map((element) => element.offsetHeight)));
        updateOffset();
        const resizeObserver = new ResizeObserver(updateOffset);
        banners.forEach((element) => resizeObserver.observe(element));
        return () => resizeObserver.disconnect();
    }, [showDateHeaders]);

    function getDateLabel(date: string): string {
        if (isDateToday(date)) {
            return t('program.today');
        }
        return format.dateTime.formatDate(new Date(date), {
            day: '2-digit',
            weekday: 'short',
            month: '2-digit'
        });
    }

    return (
        <div className="flex flex-col">
            {/* The backdrop of the sticky date headers, shared by all of them: only ever one header
             *  is stuck at the band, and the others have nothing scrolling behind them, so a single
             *  layer at the band is enough - the headers themselves stay transparent.
             *
             *  It hides the rows passing behind by reproducing the page background instead of using a
             *  colour of its own, which no single colour could match: the page background is a
             *  viewport-sized gradient (plus the cinema's background image), so it differs at every
             *  position. `PageBackgroundLayers` renders exactly the layers the page renders, fixed and
             *  viewport-sized, and `clip-path` is what cuts the band out of them.
             *
             *  `overflow-hidden` cannot do that cutting on its own, because overflow only clips
             *  descendants whose containing block chain runs through the element, and a fixed child's
             *  containing block is the viewport; it is kept because WebKit clips such a child on
             *  overflow alone. Neither may be swapped for `contain`, `filter` or `transform`: those
             *  would make the band the layers' containing block and collapse the background onto it.
             *
             *  The zero-height wrapper keeps the band out of the flow, and the invisible placeholder
             *  gives it the height of a header without measuring one - it carries the same vertical
             *  padding and text classes, so both are always the same height. */}
            {showDateHeaders && (
                <div
                    aria-hidden
                    className="sticky z-[9] h-0"
                    style={{ top: stickyTopOffset }}>
                    <div className="absolute inset-x-0 top-0 py-8 text-body-bold-14 overflow-hidden [clip-path:inset(0)]">
                        <span className="invisible">&nbsp;</span>
                        <PageBackgroundLayers
                            style={domainConfiguration.style}
                            customStyle={customStyle}
                        />
                    </div>
                </div>
            )}
            {groups.slice(0, visibleCount).map((group) => (
                // Each group is an element of its own so that its header sticks within the group
                // rather than across the whole list. Otherwise the outgoing header stays pinned at
                // the band and its label shows through the incoming one, the headers being
                // transparent; bounded by its group, it is pushed out of the band exactly as the
                // next group's header arrives there.
                <div
                    className="flex flex-col"
                    key={`ProgramCompactGroup#${group.date ?? 'all'}`}>
                    {group.date && (
                        <div
                            // The separator is an inset shadow rather than a border-b so it is drawn
                            // inside the header box and does not add to its height, which would
                            // otherwise shift the rows below every sticky header by 1px.
                            className="sticky z-10 py-8 text-body-bold-14 shadow-[inset_0_-1px_0_0_var(--surface-color)]"
                            style={{ top: stickyTopOffset }}>
                            {getDateLabel(group.date)}
                        </div>
                    )}
                    {group.showings.map((showing) => (
                        <ProgramCompactListItem
                            key={`ProgramCompactListItem#${showing.id}`}
                            showing={showing}
                            content={contentById.get(showing.contentId)}
                            showDate={showDatePerItem}
                            alignTicketButtonWithHighlights={props.hasHighlights}
                            isDesktop={props.isDesktop}
                        />
                    ))}
                </div>
            ))}
            {hasMore && (
                <div
                    className="loader-element"
                    ref={loadMoreRef}>
                    <ProgramShowingListSkeleton
                        items={1}
                        selectedDate={selectedDate}
                    />
                </div>
            )}
        </div>
    );
}

export default ProgramCompactList;
